import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  fetchCoupons,
  designListTemplates,
  designGetTemplate,
  emailPreviewContext,
  sendEmail,
  createCampaign,
  listCampaigns,
  campaignAction,
  listSmtpSenders,
  listMsSenders,
  fetchLastOrders,
  formsList,
  formEmbedPreview,
} from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Send,
  Variable,
  AlertCircle,
  Eye,
  Pencil,
  Code,
  Zap,
  Timer,
  Pause,
  Play,
  X as XIcon,
  ListChecks,
} from "lucide-react";
import LastOrderLoader from "@/components/LastOrderLoader";

const VARIABLES = [
  { group: "Recipient", items: [
    { token: "first_name", label: "First name" },
    { token: "last_name", label: "Last name" },
    { token: "full_name", label: "Full name" },
    { token: "email", label: "Email address" },
    { token: "phone", label: "Phone" },
    { token: "customer_company", label: "Their company" },
    { token: "LAST_ORDER", label: "Their last order (items + total)" },
  ]},
  { group: "Coupon", items: [
    { token: "coupon_code", label: "Coupon code" },
    { token: "coupon_amount", label: "Discount" },
    { token: "coupon_link", label: "Share link" },
    { token: "coupon_expiry", label: "Expiry date" },
  ]},
  { group: "You", items: [
    { token: "dealer_name", label: "Your name" },
    { token: "dealer_first_name", label: "Your first name" },
    { token: "company", label: "Company" },
    { token: "dealer_phone", label: "Your phone" },
    { token: "dealer_email", label: "Your email" },
    { token: "dealer_shop_url", label: "Your shop link" },
    { token: "dealer_instagram", label: "Your Instagram" },
    { token: "today", label: "Today's date" },
  ]},
  { group: "Order (order trigger)", items: [
    { token: "ORDER_NUMBER", label: "Order #" },
    { token: "ORDER_STATUS", label: "Status" },
    { token: "ORDER_DATE", label: "Order date" },
    { token: "ORDER_ITEMS", label: "Items (photo · price · MSRP)" },
    { token: "REVIEW_ITEMS", label: "Review buttons (per product · earn rewards)" },
    { token: "REVIEW_POINTS", label: "Review reward ($)" },
    { token: "ORDER_SUBTOTAL", label: "Subtotal" },
    { token: "ORDER_SHIPPING", label: "Shipping $" },
    { token: "ORDER_TAX", label: "Tax" },
    { token: "ORDER_TOTAL", label: "Total" },
    { token: "SHIPPING_ADDRESS", label: "Shipping address" },
    { token: "BILLING_ADDRESS", label: "Billing address" },
    { token: "ORDER_WEIGHT", label: "Weight" },
    { token: "PAYMENT_METHOD", label: "Payment" },
    { token: "TRACKING_NUMBER", label: "Tracking #" },
    { token: "TRACKING_URL", label: "Tracking link" },
    { token: "TRACKING_BLOCK", label: "Tracking box (only if shipped)" },
    { token: "TRACKING_TABLE", label: "Airtable tracking row" },
    { token: "SHIP_NOTICE", label: "'Tracking coming' note (if not shipped)" },
    { token: "CARRIER", label: "Carrier (UPS)" },
    { token: "CUSTOMER_NAME", label: "Customer name (ambassador alert)" },
    { token: "CUSTOMER_EMAIL", label: "Customer email" },
    { token: "CUSTOMER_PHONE", label: "Customer phone" },
    { token: "AMBASSADOR_NAME", label: "Ambassador name" },
    { token: "AMBASSADOR_LINE", label: "Ambassador line (if any)" },
    { token: "RETURN_POLICY", label: "Return policy block" },
  ]},
];

const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

// Branded, on-brand coupon block inserted into the email body when a coupon is
// attached. Tokens are rendered per-recipient at send time.
const COUPON_BLOCK = `
<div style="margin:26px 0;text-align:center;">
  <div style="display:inline-block;border:2px dashed #b88a2b;border-radius:14px;padding:22px 30px;background:#f6f4ef;">
    <div style="font-size:12px;letter-spacing:2px;color:#b88a2b;text-transform:uppercase;font-weight:bold;">Your exclusive offer</div>
    <div style="font-size:32px;font-weight:bold;color:#0b2545;margin:6px 0;">{{coupon_amount}}</div>
    <div style="font-size:15px;color:#333333;">Use code <strong style="color:#0b2545;letter-spacing:1px;">{{coupon_code}}</strong></div>
    <a href="{{coupon_link}}" style="display:inline-block;margin-top:14px;background:#b88a2b;color:#ffffff;text-decoration:none;font-weight:bold;padding:11px 26px;border-radius:8px;text-transform:uppercase;font-size:13px;letter-spacing:1px;">Shop Now</a>
    <div style="font-size:12px;color:#9ca3af;margin-top:10px;">Expires {{coupon_expiry}}</div>
  </div>
</div>
`;

function renderTokens(text, ctx) {
  if (!text) return "";
  return text.replace(TOKEN_RE, (_, k) => {
    const v = ctx[k];
    return v == null ? "" : String(v);
  });
}

function buildContext(contact, dealer, coupon) {
  const c = coupon || {};
  const amount = c.amount
    ? (c.coupon_type === "percent" ? `${Math.round(c.amount)}% off` : `$${c.amount} off`)
    : "";
  const link = c.coupon_name
    ? `https://orevital.com/?coupon=${c.coupon_name}`
    : "";
  return {
    first_name: contact?.first_name || "",
    last_name: contact?.last_name || "",
    full_name: contact?.full_name || `${contact?.first_name || ""} ${contact?.last_name || ""}`.trim() || contact?.email || "",
    email: contact?.email || "",
    coupon_code: c.coupon_name || "",
    coupon_amount: amount,
    coupon_link: link,
    coupon_expiry: c.expiry_date || "",
    coupon_tier: c.coupon_tier || "",
    dealer_first_name: dealer?.first_name || "",
    dealer_last_name: dealer?.last_name || "",
    dealer_name: `${dealer?.first_name || ""} ${dealer?.last_name || ""}`.trim(),
    company: dealer?.company_name || dealer?.company || "",
    today: new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
  };
}

export default function EmailSendModal({ open, onOpenChange, recipients: selectedRecipients = [], preset = null }) {
  const { user } = useAuth();
  const userId = String(user?.user_id || user?.id || "");
  const recId = user?.user_record_id || user?.record_id;

  // Order trigger: when set, these (one per order) replace the CRM-selected contacts.
  const [orderRecipients, setOrderRecipients] = useState(null);
  const recipients = orderRecipients ?? selectedRecipients;
  const orderMode = orderRecipients !== null;

  const [templates, setTemplates] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [selectedTemplateId, setSelectedTemplateId] = useState("custom");
  const [loadingTpl, setLoadingTpl] = useState(null);
  const [selectedCouponId, setSelectedCouponId] = useState("none");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // Delivery mode: "now" (blast) or "drip" (throttled campaign queue)
  const [deliveryMode, setDeliveryMode] = useState("now");
  const [ratePerMin, setRatePerMin] = useState("1");
  const [startAt, setStartAt] = useState(""); // datetime-local; empty = start now
  const [showQueue, setShowQueue] = useState(false);
  // Admin: choose which SMTP mailbox to send from ("" = default connected mailbox).
  const [fromAccountId, setFromAccountId] = useState("");
  const [senderOptions, setSenderOptions] = useState([]);
  // Refs for inserting variables at cursor
  const [activeField, setActiveField] = useState("body");
  // Real merge values for the first recipient (cart HTML, email_id, unsubscribe…)
  const [serverCtx, setServerCtx] = useState({});
  // In-email forms: picker + server-rendered preview blocks keyed by form id
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState("none");
  const [formPreviews, setFormPreviews] = useState({});

  useEffect(() => {
    if (!open || !recId) return;
    formsList(recId).then((r) => setForms((r?.forms || []).filter((f) => f.active))).catch(() => setForms([]));
  }, [open, recId]);

  const formRefsInBody = useMemo(() => {
    const refs = new Set();
    for (const m of (body || "").matchAll(/\{\{\s*FORM_EMBED\s*:\s*([^}\s]+)\s*\}\}/gi)) refs.add(m[1].toLowerCase());
    return [...refs];
  }, [body]);
  useEffect(() => {
    const missing = formRefsInBody.filter((ref) => !formPreviews[ref]);
    if (!missing.length || !recId) return;
    missing.forEach((ref) => {
      formEmbedPreview(recId, ref)
        .then((r) => setFormPreviews((p) => ({ ...p, [ref]: r.html || "" })))
        .catch(() => setFormPreviews((p) => ({ ...p, [ref]: "" })));
    });
  }, [formRefsInBody, recId]); // eslint-disable-line react-hooks/exhaustive-deps

  function insertForm(formId) {
    setSelectedFormId(formId);
    if (!formId || formId === "none") return;
    const f = forms.find((x) => x.id === formId);
    if (!f) return;
    const token = `{{FORM_EMBED:${f.form_id}}}`;
    setBody((b) => (b.includes(token) ? b : `${b}\n${token}\n`));
    toast.success(`${f.name} inserted — each contact gets their own personal form link`);
  }

  useEffect(() => {
    if (!open) {
      setOrderRecipients(null);
      return;
    }
    setLoading(true);
    Promise.all([designListTemplates(recId), fetchCoupons(userId)])
      .then(([t, c]) => {
        setTemplates(t.items || []);
        setCoupons(c.coupons || []);
        // Default coupon = first active
    // Auto-suggest drip for large batches so big sends don't clog the mailbox.
    setDeliveryMode(recipients.length > 50 ? "drip" : "now");
    // Load admin send-from accounts (returns [] for non-admins): OAuth Outlook
    // mailboxes plus any legacy manual SMTP senders.
    Promise.all([
      listMsSenders(recId).catch(() => []),
      listSmtpSenders(recId).catch(() => []),
    ]).then(([msSenders, smtpSenders]) => {
      setSenderOptions([
        ...msSenders.map((s) => ({ id: s.id, label: s.label || s.email, from_email: s.email })),
        ...smtpSenders,
      ]);
    });
        setSelectedCouponId("none");   // no coupon by default — the dealer picks one explicitly
      })
      .catch(() => toast.error("Could not load templates"))
      .finally(() => setLoading(false));
    // Default body
    if (preset?.subject) setSubject(preset.subject);
    if (preset?.body) setBody(preset.body);
    if (!subject && !preset?.subject) {
      setSubject(`A little something from ${user?.first_name || "us"}`);
    }
    if (!body && !preset?.body) {
      setBody(
        `<p>Hi {{first_name}},</p>\n` +
        `<p>I wanted to share a quick update from {{company}} with you.</p>\n` +
        `<p><a href="{{dealer_shop_url}}">Visit my OreVital shop →</a></p>\n` +
        `<p>— {{dealer_name}}</p>`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedCoupon = useMemo(
    () => coupons.find((c) => c.id === selectedCouponId) || null,
    [coupons, selectedCouponId]
  );

  // Fetch the first recipient's real merge data (pending cart + total, email_id,
  // unsubscribe URL) so the Preview shows the actual contents that will be sent.
  const firstRecipientId = recipients[0]?.id;
  const firstOrderId = recipients[0]?.order_record_id || "";
  useEffect(() => {
    if (!open || !firstRecipientId) {
      setServerCtx({});
      return;
    }
    emailPreviewContext(recId, firstRecipientId, firstOrderId)
      .then((res) => setServerCtx(res.context || {}))
      .catch(() => setServerCtx({}));
  }, [open, firstRecipientId, firstOrderId, recId]);

  async function applyTemplate(id) {
    setSelectedTemplateId(id);
    if (id === "custom") return;
    setLoadingTpl(id);
    try {
      const t = await designGetTemplate(id, recId);
      if (t.subject) setSubject(t.subject);
      else if (t.title) setSubject(t.title);
      if (t.html) setBody(t.html);
    } catch {
      toast.error("Could not load template");
    } finally {
      setLoadingTpl(null);
    }
  }

  // Last orders loaded → recipients become one row per order; auto-pick the
  // matching shared template unless the user already chose one:
  // all Completed → "Order Shipped", else "Order Confirmation".
  function onLastOrdersLoaded(list) {
    setOrderRecipients(list);
    if (list && list.length && selectedTemplateId === "custom") {
      const allShipped = list.every((r) => r.status === "completed");
      const want = allShipped ? /order shipped/i : /order confirmation/i;
      const t = templates.find((x) => want.test(x.title || ""));
      if (t) applyTemplate(t.id);
    }
  }

  function insertVariable(token) {
    const placeholder = `{{${token}}}`;
    if (activeField === "subject") {
      setSubject((s) => (s ? s + " " + placeholder : placeholder));
    } else {
      setBody((b) => b + placeholder);
    }
  }

  const previewContact = recipients[0] || {};
  const ctx = {
    // Server-rendered merge values (order tokens, cart HTML, unsubscribe…)
    ...serverCtx,
    ...buildContext(previewContact, user, selectedCoupon),
    PENDING_CART_HTML: serverCtx.PENDING_CART_HTML || "",
    email_id: serverCtx.email_id || previewContact.email || "",
    UNSUBSCRIBE_URL: serverCtx.UNSUBSCRIBE_URL || "",
  };
  const previewSubject = renderTokens(subject, ctx);
  const previewBody = renderTokens(
    body.replace(/\{\{\s*FORM_EMBED\s*:\s*([^}\s]+)\s*\}\}/gi, (m, ref) => formPreviews[ref.toLowerCase()] ?? `<div style="padding:18px;border:1px dashed #d6ad52;border-radius:12px;color:#6b7280;font-size:13px;text-align:center;">Loading form preview…</div>`),
    ctx
  );

  async function onSend() {
    if (!subject.trim()) return toast.error("Subject is required");
    if (!body.trim()) return toast.error("Body is required");
    if (recipients.length === 0) return toast.error("No recipients");

    const baseRecipients = recipients.map((r) => ({
      id: r.id,
      email: r.email,
      first_name: r.first_name,
      last_name: r.last_name,
      full_name: r.full_name,
      phone: r.phone,
      contact_record_id: r.contact_record_id || r.id,
      order_record_id: r.order_record_id || null,
    }));
    const templateName =
      selectedTemplateId === "custom"
        ? "custom"
        : templates.find((t) => t.id === selectedTemplateId)?.title || "custom";
    const payload = {
      user_record_id: recId,
      subject,
      html: body,
      template_name: templateName,
      coupon_id: selectedCouponId === "none" ? null : selectedCouponId,
      recipients: baseRecipients,
      from_account_id: fromAccountId || null,
    };
    setSending(true);
    try {
      if (deliveryMode === "drip") {
        const res = await createCampaign({
          ...payload,
          rate_per_min: Number(ratePerMin) || 1,
          start_at: startAt ? new Date(startAt).toISOString() : null,
        });
        toast.success(
          `Queued ${res.total} email${res.total === 1 ? "" : "s"} · ${res.rate_per_min}/min` +
            (startAt ? " · scheduled" : "")
        );
        setShowQueue(true);
      } else {
        const res = await sendEmail(payload);
        if (res.sent && !res.failed) {
          toast.success(`Sent to ${res.sent} ${res.sent === 1 ? "person" : "people"}`);
        } else if (res.sent) {
          toast.message(`Sent ${res.sent}, ${res.failed} failed`);
        } else {
          toast.error(`All ${res.failed} sends failed`);
        }
        onOpenChange(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Send failed";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md sm:max-w-lg p-0 rounded-2xl overflow-hidden flex flex-col max-h-[92dvh]"
        data-testid="email-send-modal"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-100 shrink-0">
          <DialogTitle className="text-xl" data-testid="email-modal-title">
            {orderMode
              ? `Email ${recipients.length} ${recipients.length === 1 ? "order" : "orders"}`
              : `Email ${recipients.length} ${recipients.length === 1 ? "contact" : "contacts"}`}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {orderMode
              ? "Order trigger: each order gets its own email with the customer's items, addresses and totals."
              : "Sent from your selected mailbox in Profile → Settings. Each contact gets their own personalized copy."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-10">
            <Loader2 className="animate-spin text-zinc-400" />
          </div>
        ) : (
          <Tabs defaultValue="compose" className="flex-1 flex flex-col overflow-hidden">
            <div className="px-5 pt-3 shrink-0">
              <TabsList className="grid grid-cols-2 bg-zinc-100 rounded-xl p-1 h-10 w-full">
                <TabsTrigger
                  value="compose"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs font-medium gap-1"
                  data-testid="email-tab-compose"
                >
                  <Pencil size={12} /> Compose
                </TabsTrigger>
                <TabsTrigger
                  value="preview"
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs font-medium gap-1"
                  data-testid="email-tab-preview"
                >
                  <Eye size={12} /> Preview
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="compose" className="overflow-y-auto px-5 py-4 space-y-3 mt-0 flex-1">
              {/* Load each contact's last order → order-confirmation / shipped merge */}
              <LastOrderLoader
                recId={recId}
                contacts={selectedRecipients}
                loaded={orderRecipients}
                onLoaded={onLastOrdersLoaded}
                onClear={() => setOrderRecipients(null)}
              />

              {/* Template thumbnails */}
              <div>
                <Label className="text-xs font-medium text-zinc-700">Template</Label>
                <div
                  className="mt-1.5 flex gap-2 overflow-x-auto pb-1.5"
                  data-testid="email-template-gallery"
                >
                  <button
                    type="button"
                    onClick={() => applyTemplate("custom")}
                    className={`shrink-0 w-[88px] text-left rounded-lg border p-1.5 transition-colors ${
                      selectedTemplateId === "custom"
                        ? "border-violet-500 ring-1 ring-violet-300 bg-violet-50/40"
                        : "border-zinc-200 hover:border-zinc-300"
                    }`}
                    data-testid="email-template-custom"
                  >
                    <div className="w-full h-14 rounded-md bg-zinc-100 flex items-center justify-center">
                      <Pencil size={16} className="text-zinc-400" />
                    </div>
                    <div className="text-[10px] mt-1 font-medium text-zinc-700 truncate">Custom</div>
                  </button>
                  {templates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => applyTemplate(t.id)}
                      className={`relative shrink-0 w-[88px] text-left rounded-lg border p-1.5 transition-colors ${
                        selectedTemplateId === t.id
                          ? "border-violet-500 ring-1 ring-violet-300 bg-violet-50/40"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                      data-testid={`email-template-${t.id}`}
                    >
                      <div className="w-full h-14 rounded-md bg-zinc-100 overflow-hidden flex items-center justify-center">
                        {t.image_url ? (
                          <img
                            src={t.image_url}
                            alt={t.title}
                            className="w-full h-full object-cover"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        ) : (
                          <Code size={15} className="text-zinc-400" />
                        )}
                      </div>
                      <div className="text-[10px] mt-1 font-medium text-zinc-700 truncate">{t.title}</div>
                      {loadingTpl === t.id && (
                        <div className="absolute inset-0 rounded-lg bg-white/60 flex items-center justify-center">
                          <Loader2 className="animate-spin text-violet-600" size={16} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-400 mt-0.5">
                  Pick a template to load it below, then edit and send.
                </p>
              </div>

              {/* Coupon */}
              <div>
                <Label className="text-xs font-medium text-zinc-700">Attach coupon</Label>
                <Select value={selectedCouponId} onValueChange={setSelectedCouponId}>
                  <SelectTrigger className="h-10 rounded-lg mt-1" data-testid="email-coupon-select">
                    <SelectValue placeholder="Pick a coupon" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No coupon</SelectItem>
                    {coupons.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.coupon_name} · {c.coupon_tier || "—"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  disabled={selectedCouponId === "none"}
                  onClick={() => setBody((b) => b + COUPON_BLOCK)}
                  className="mt-2 w-full h-9 rounded-lg text-xs font-medium border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="email-insert-coupon-btn"
                >
                  + Insert coupon block into email
                </button>
              </div>

              {/* In-email form */}
              {forms.length > 0 && (
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Insert a form</Label>
                  <Select value={selectedFormId} onValueChange={insertForm}>
                    <SelectTrigger className="h-10 rounded-lg mt-1" data-testid="email-form-select">
                      <SelectValue placeholder="Pick a form" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No form</SelectItem>
                      {forms.map((f) => (
                        <SelectItem key={f.id} value={f.id} data-testid={`email-form-option-${f.form_id}`}>
                          {f.name} · {f.form_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-zinc-400 mt-1">
                    Adds the form (your photo header, option cards, button) to the message. Each contact gets a personal link; the standard footer stays.
                  </p>
                </div>
              )}

              {/* From account (admin multi-SMTP) */}
              {senderOptions.length > 0 && (
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Send from</Label>
                  <Select value={fromAccountId || "default"} onValueChange={(v) => setFromAccountId(v === "default" ? "" : v)}>
                    <SelectTrigger className="mt-1 h-10 rounded-lg" data-testid="email-from-account">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Your connected mailbox</SelectItem>
                      {senderOptions.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`email-from-${s.id}`}>
                          {s.label} · {s.from_email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Delivery mode: blast now vs throttled drip queue */}
              <div>
                <Label className="text-xs font-medium text-zinc-700">Delivery</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="email-delivery-mode">
                  <button
                    type="button"
                    onClick={() => setDeliveryMode("now")}
                    className={`flex items-center gap-2 rounded-lg border p-2.5 text-left transition-colors ${
                      deliveryMode === "now"
                        ? "border-violet-500 ring-1 ring-violet-300 bg-violet-50/40"
                        : "border-zinc-200 hover:border-zinc-300"
                    }`}
                    data-testid="email-mode-now"
                  >
                    <Zap size={16} className="text-violet-600 shrink-0" />
                    <div>
                      <div className="text-xs font-semibold text-zinc-800">Send now</div>
                      <div className="text-[10px] text-zinc-500">All at once</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeliveryMode("drip")}
                    className={`flex items-center gap-2 rounded-lg border p-2.5 text-left transition-colors ${
                      deliveryMode === "drip"
                        ? "border-violet-500 ring-1 ring-violet-300 bg-violet-50/40"
                        : "border-zinc-200 hover:border-zinc-300"
                    }`}
                    data-testid="email-mode-drip"
                  >
                    <Timer size={16} className="text-emerald-600 shrink-0" />
                    <div>
                      <div className="text-xs font-semibold text-zinc-800">Drip queue</div>
                      <div className="text-[10px] text-zinc-500">Throttled, scheduled</div>
                    </div>
                  </button>
                </div>
                {deliveryMode === "drip" && (
                  <div className="mt-2 grid grid-cols-2 gap-2" data-testid="email-drip-options">
                    <div>
                      <Label className="text-[10px] text-zinc-500">Rate</Label>
                      <Select value={ratePerMin} onValueChange={setRatePerMin}>
                        <SelectTrigger className="h-9 rounded-lg mt-0.5 text-xs" data-testid="email-rate-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 / minute</SelectItem>
                          <SelectItem value="5">5 / minute</SelectItem>
                          <SelectItem value="10">10 / minute</SelectItem>
                          <SelectItem value="30">30 / minute</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-zinc-500">Start (optional)</Label>
                      <Input
                        type="datetime-local"
                        value={startAt}
                        onChange={(e) => setStartAt(e.target.value)}
                        className="h-9 rounded-lg mt-0.5 text-xs"
                        data-testid="email-start-input"
                      />
                    </div>
                    <p className="col-span-2 text-[10px] text-zinc-400">
                      {recipients.length} emails · ~
                      {Math.ceil(recipients.length / (Number(ratePerMin) || 1))} min to finish
                      {startAt ? " · starts at your chosen time" : " · starts immediately"}.
                    </p>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  onFocus={() => setActiveField("subject")}
                  className="h-10 rounded-lg mt-1"
                  data-testid="email-subject-input"
                />
              </div>

              {/* Body */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-zinc-700">Message</Label>
                  <span className="text-[10px] text-zinc-400">HTML supported</span>
                </div>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onFocus={() => setActiveField("body")}
                  rows={8}
                  className="rounded-lg mt-1 font-mono text-xs"
                  data-testid="email-body-input"
                />
              </div>

              {/* Variable picker */}
              <div>
                <Label className="text-xs font-medium text-zinc-700 flex items-center gap-1.5">
                  <Variable size={12} /> Insert variable into{" "}
                  <span className="text-violet-700 font-semibold">
                    {activeField}
                  </span>
                </Label>
                <div className="mt-2 space-y-2" data-testid="email-variables">
                  {VARIABLES.map((grp) => (
                    <div key={grp.group}>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">
                        {grp.group}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {grp.items.map((v) => (
                          <button
                            key={v.token}
                            type="button"
                            onClick={() => insertVariable(v.token)}
                            className="text-[11px] px-2 py-1 rounded-md bg-violet-50 text-violet-700 hover:bg-violet-100 font-medium transition-colors"
                            data-testid={`variable-${v.token}`}
                          >
                            {v.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="preview" className="overflow-y-auto px-5 py-4 mt-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">
                Previewing for {previewContact?.full_name || previewContact?.email || "first contact"}
              </div>
              <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
                <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50 text-xs">
                  <div className="text-zinc-500">Subject</div>
                  <div className="font-semibold text-zinc-900" data-testid="email-preview-subject">
                    {previewSubject || "(no subject)"}
                  </div>
                </div>
                <div
                  className="prose prose-sm max-w-none px-3 py-3 text-sm"
                  dangerouslySetInnerHTML={{ __html: previewBody }}
                  data-testid="email-preview-body"
                />
              </div>
              {recipients.length > 1 && (
                <div className="mt-3 rounded-lg bg-violet-50 border border-violet-200 p-2.5 text-xs text-violet-800 flex items-start gap-2">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  Each of the {recipients.length} contacts will receive their own version
                  with their name and details filled in.
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Footer / Send */}
        <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50 flex gap-2 shrink-0">
          <Button
            variant="ghost"
            onClick={() => setShowQueue(true)}
            data-testid="email-queue-btn"
            title="View sending queue"
          >
            <ListChecks size={16} className="mr-1.5" /> Queue
          </Button>
          <Button
            onClick={onSend}
            disabled={sending || loading}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11"
            data-testid="email-send-btn"
          >
            {sending ? (
              <Loader2 className="animate-spin mr-2" size={16} />
            ) : deliveryMode === "drip" ? (
              <Timer size={16} className="mr-2" />
            ) : (
              <Send size={16} className="mr-2" />
            )}
            {deliveryMode === "drip" ? `Queue ${recipients.length}` : `Send ${recipients.length}`}
          </Button>
        </div>
        <CampaignsQueue open={showQueue} onClose={() => setShowQueue(false)} recId={recId} />
      </DialogContent>
    </Dialog>
  );
}

const STATUS_STYLES = {
  running: "bg-emerald-100 text-emerald-700",
  paused: "bg-amber-100 text-amber-700",
  completed: "bg-zinc-100 text-zinc-600",
  cancelled: "bg-rose-100 text-rose-700",
  scheduled: "bg-violet-100 text-violet-700",
};

function CampaignsQueue({ open, onClose, recId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  async function refresh() {
    if (!recId) return;
    try {
      const data = await listCampaigns(recId);
      setItems(data.campaigns || []);
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    refresh().finally(() => setLoading(false));
    // Live-poll while there's an active campaign.
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recId]);

  async function act(id, action) {
    try {
      await campaignAction(id, recId, action);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 rounded-2xl overflow-hidden flex flex-col max-h-[85dvh]" data-testid="campaigns-queue-modal">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-100">
          <DialogTitle className="text-lg">Sending queue</DialogTitle>
          <DialogDescription className="text-xs">
            Throttled campaigns send in the background — safe to close this window.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto px-5 py-4 space-y-2.5">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-violet-600" /></div>
          ) : items.length === 0 ? (
            <p className="text-center text-sm text-zinc-400 py-8" data-testid="campaigns-empty">
              No campaigns yet. Pick "Drip queue" when sending to schedule one.
            </p>
          ) : (
            items.map((c) => {
              const pct = c.total ? Math.round(((c.sent_count + c.failed_count) / c.total) * 100) : 0;
              return (
                <div key={c.id} className="rounded-xl border border-zinc-200 p-3" data-testid="campaign-row">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{c.subject || "(no subject)"}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">
                        {c.sent_count}/{c.total} sent
                        {c.failed_count ? ` · ${c.failed_count} failed` : ""} · {c.rate_per_min}/min
                      </p>
                    </div>
                    <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[c.status] || "bg-zinc-100 text-zinc-600"}`}>
                      {c.status}
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  {(c.status === "running" || c.status === "paused") && (
                    <div className="mt-2.5 flex gap-2">
                      {c.status === "running" ? (
                        <Button size="sm" variant="outline" onClick={() => act(c.id, "pause")} data-testid="campaign-pause">
                          <Pause size={13} className="mr-1" /> Pause
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => act(c.id, "resume")} data-testid="campaign-resume">
                          <Play size={13} className="mr-1" /> Resume
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => act(c.id, "cancel")} data-testid="campaign-cancel">
                        <XIcon size={13} className="mr-1" /> Cancel
                      </Button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
